/* eslint-disable react/no-array-index-key */
import { Flex, Tag, Typography } from '@duxton-web/ui';
import React from 'react';

import { lineDiffOps, wordDiffOps } from './adapters';

// Inline word-level diff → [activeNodes, pendingNodes]; removed words show on
// the active (old) side in red/strikethrough, added words on the pending (new)
// side in green.
const wordSides = (oldStr, newStr) => {
  const oldN = [];
  const newN = [];
  wordDiffOps(oldStr, newStr).forEach((op, i) => {
    if (op.type === 'eq') {
      oldN.push(<span key={i}>{op.text}</span>);
      newN.push(<span key={i}>{op.text}</span>);
    } else if (op.type === 'del') {
      oldN.push(<span key={i} className="cc-diff-del">{op.text}</span>);
    } else {
      newN.push(<span key={i} className="cc-diff-add">{op.text}</span>);
    }
  });
  return [oldN, newN];
};

// Line-level diff (for the CTA list) → [activeBlocks, pendingBlocks].
const lineSides = (oldStr, newStr) => {
  const oldN = [];
  const newN = [];
  lineDiffOps(oldStr, newStr).forEach((op, i) => {
    if (op.type === 'eq') {
      oldN.push(<div key={i} className="cc-diff-line">{op.text}</div>);
      newN.push(<div key={i} className="cc-diff-line">{op.text}</div>);
    } else if (op.type === 'del') {
      oldN.push(<div key={i} className="cc-diff-line cc-diff-line--del">{op.text}</div>);
    } else {
      newN.push(<div key={i} className="cc-diff-line cc-diff-line--add">{op.text}</div>);
    }
  });
  return [oldN, newN];
};

const plainLines = (s) => (s || '').split('\n').map((l) => l.trim()).filter(Boolean);

const statusTag = (label, color) => (color ? <Tag color={color}>{label}</Tag> : <Tag>{label}</Tag>);

// One diff field: a label with a Changed/No change tag + char/line meta, then a
// two-column git-style word/line diff when changed, or a single neutral box when
// unchanged. Status labels default to the pending-approval comparison.
const DiffField = ({
  activeLabel = 'ACTIVE',
  activeTagColor = 'error',
  label,
  pendingLabel = 'PENDING APPROVAL',
  pendingTagColor = 'success',
  activeVal,
  pendingVal,
  mode = 'word',
}) => {
  const a = activeVal || '';
  const p = pendingVal || '';
  const changed = a !== p;
  const isLine = mode === 'line';
  const aLines = isLine ? plainLines(a) : [];
  const meta = isLine
    ? `${aLines.length} → ${plainLines(p).length} lines`
    : `${a.length} → ${p.length} chars`;
  const [oldNodes, newNodes] = isLine ? lineSides(a, p) : wordSides(a, p);

  let noChange;
  if (isLine && aLines.length) {
    noChange = aLines.map((l, i) => <div key={i} className="cc-diff-line">{l}</div>);
  } else {
    noChange = <Typography.Body level={2} type="secondary">{a || '—'}</Typography.Body>;
  }

  return (
    <div className="cc-diff-field">
      <Flex align="center" gap={8} wrap="wrap" className="cc-diff-heading">
        <Typography.Body level={2} strong>{label}</Typography.Body>
        {changed ? <Tag color="warning">Changed</Tag> : <Tag>No change</Tag>}
        {changed && <Typography.Metadata type="secondary">{meta}</Typography.Metadata>}
      </Flex>
      {changed ? (
        <div className="cc-diff-cols">
          <Flex vertical gap={6} className="cc-diff-side">
            {statusTag(activeLabel, activeTagColor)}
            <div className="cc-diff-box cc-diff-box--active">
              {isLine
                ? oldNodes
                : <Typography.Body level={2}>{oldNodes.length ? oldNodes : '—'}</Typography.Body>}
            </div>
          </Flex>
          <Flex vertical gap={6} className="cc-diff-side">
            {statusTag(pendingLabel, pendingTagColor)}
            <div className="cc-diff-box cc-diff-box--pending">
              {isLine
                ? newNodes
                : <Typography.Body level={2}>{newNodes.length ? newNodes : '—'}</Typography.Body>}
            </div>
          </Flex>
        </div>
      ) : (
        <div className="cc-diff-box cc-diff-nochange">{noChange}</div>
      )}
    </div>
  );
};

// Chip-based diff for link lists (CVP / Attribute / Persona / Audience): kept items as
// plain chips, removed as red strikethrough, added as green "+" chips.
export type DiffChipsProps = {
  label: string;
  active?: string[];
  pending?: string[];
  labelForValue?: (value: string) => React.ReactNode;
};

export const DiffChips = ({
  label,
  active = [],
  pending = [],
  labelForValue = (value) => value,
}: DiffChipsProps) => {
  const activeSet = new Set(active);
  const pendingSet = new Set(pending);
  const kept = active.filter((x) => pendingSet.has(x));
  const removed = active.filter((x) => !pendingSet.has(x));
  const added = pending.filter((x) => !activeSet.has(x));
  const changed = removed.length > 0 || added.length > 0;

  return (
    <div className="cc-diff-field">
      <Flex align="center" gap={8} wrap="wrap" className="cc-diff-heading">
        <Typography.Body level={2} strong>{label}</Typography.Body>
        <Typography.Metadata type="secondary">{active.length} → {pending.length}</Typography.Metadata>
        {changed ? <Tag color="warning">Changed</Tag> : <Tag>No change</Tag>}
      </Flex>
      {changed ? (
        <div className="cc-diff-box cc-diff-nochange">
          <Flex gap={8} wrap="wrap">
            {kept.map((k) => <Tag key={k}>{labelForValue(k)}</Tag>)}
            {removed.map((k) => (
              <Tag key={k} color="error" className="cc-diff-chip-removed">
                − {labelForValue(k)}
              </Tag>
            ))}
            {added.map((k) => <Tag key={k} color="success">+ {labelForValue(k)}</Tag>)}
          </Flex>
        </div>
      ) : (
        <div className="cc-diff-box cc-diff-nochange">
          {active.length ? (
            <Flex gap={8} wrap="wrap">
              {active.map((k) => <Tag key={k}>{labelForValue(k)}</Tag>)}
            </Flex>
          ) : (
            <Typography.Body level={2} type="secondary">No change</Typography.Body>
          )}
        </div>
      )}
    </div>
  );
};

export default DiffField;
